import { useSyncExternalStore } from 'react';
import { getTour } from './registry';
import type { TourDefinition, TourTrigger } from './types';

export interface ActiveTour {
  tour: TourDefinition;
  trigger: TourTrigger;
}

let active: ActiveTour | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** One tour at a time; unknown ids and a tour already running are no-ops. */
export function startTour(id: string, trigger: TourTrigger = 'programmatic'): boolean {
  const tour = getTour(id);
  if (!tour || active) return false;
  active = { tour, trigger };
  emit();
  return true;
}

export function endTour(): void {
  if (!active) return;
  active = null;
  emit();
}

export function getActiveTour(): ActiveTour | null {
  return active;
}

export function useActiveTour(): ActiveTour | null {
  return useSyncExternalStore(subscribe, getActiveTour, () => null);
}

/** True while any tour is on screen — other prompts stay hidden. */
export function useTourActive(): boolean {
  return useActiveTour() !== null;
}

function seenKey(tour: TourDefinition): string {
  return `taboo_tour_${tour.id}_v${tour.version}`;
}

/** Storage can throw (private mode, blocked site data) — a lost flag only means a replay. */
export function markTourSeen(tour: TourDefinition): void {
  try {
    window.localStorage.setItem(seenKey(tour), new Date().toISOString());
  } catch {
    // Non-critical: see above.
  }
}

export function hasSeenTour(tour: TourDefinition): boolean {
  try {
    return window.localStorage.getItem(seenKey(tour)) !== null;
  } catch {
    return false;
  }
}

/** Test-only reset. */
export function resetTourStoreForTests(): void {
  active = null;
}
